// public/src/pages/BlogPost.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiCalendar, 
  FiUser, 
  FiClock, 
  FiArrowLeft, 
  FiShare2, 
  FiBookmark,
  FiTag,
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiLink
} from 'react-icons/fi';
import axios from 'axios';

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      // Try to fetch by slug first, then by ID
      const response = await axios.get(`https://agri-back-mauve.vercel.app/api/content/public/blog`);
      const allPosts = response.data.data;
      
      // Find the post by slug or ID
      const foundPost = allPosts.find(p => 
        p.seo?.slug === id || p._id === id
      );

      if (foundPost) {
        setPost(foundPost);
        
        // Fetch related posts (same category)
        const related = allPosts
          .filter(p => 
            p._id !== foundPost._id && 
            p.category === foundPost.category
          )
          .slice(0, 3);
        setRelatedPosts(related);

        // Increment view count
        await incrementViewCount(foundPost._id);
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      setError('Failed to load blog post');
      console.error('Error fetching blog post:', err);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (postId) => {
    try {
      await axios.put(`https://agri-back-mauve.vercel.app/api/content/admin/${postId}/views`);
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareOnSocialMedia = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    const text = post?.description || '';

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Blog Post Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/blog" className="text-green-600 hover:text-green-700 font-medium">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="container">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <FiArrowLeft size={20} />
              <span>Back to Blog</span>
            </button>
            
            <div className="flex items-center gap-4">
              {/* Share buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => shareOnSocialMedia('facebook')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on Facebook"
                >
                  <FiFacebook size={18} />
                </button>
                <button
                  onClick={() => shareOnSocialMedia('twitter')}
                  className="p-2 text-blue-400 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on Twitter"
                >
                  <FiTwitter size={18} />
                </button>
                <button
                  onClick={() => shareOnSocialMedia('linkedin')}
                  className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on LinkedIn"
                >
                  <FiLinkedin size={18} />
                </button>
                <button
                  onClick={copyToClipboard}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-full transition-colors relative"
                  title="Copy link"
                >
                  <FiLink size={18} />
                  {copied && (
                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="bg-white">
        <div className="container">
          {/* Featured Image */}
          {post.image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 rounded-xl overflow-hidden"
            >
              <img
                src={post.image.startsWith('http') ? post.image : `https://agri-back-mauve.vercel.app${post.image}`}
                alt={post.title}
                className="w-full h-96 object-cover"
              />
            </motion.div>
          )}

          {/* Article Header Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-4xl mx-auto text-center mb-12"
          >
            {/* Category */}
            {post.category && (
              <span className="inline-block bg-green-100 text-green-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
                {post.category}
              </span>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Subtitle */}
            {post.subtitle && (
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                {post.subtitle}
              </p>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-gray-500 mb-8">
              <div className="flex items-center gap-2">
                <FiUser size={16} />
                <span>{post.author || 'Afrikonsults Team'}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCalendar size={16} />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              {post.readTime && (
                <div className="flex items-center gap-2">
                  <FiClock size={16} />
                  <span>{post.readTime} min read</span>
                </div>
              )}
              {post.views > 0 && (
                <div className="flex items-center gap-2">
                  <span>{post.views} views</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full"
                  >
                    <FiTag size={12} />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </article>

      {/* Article Content */}
      <section className="bg-gray-50 py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
            >
              {/* Article Content */}
              <div className="prose prose-lg max-w-none">
                {post.content ? (
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                ) : (
                  <div className="text-gray-600 leading-relaxed space-y-6">
                    {post.description && (
                      <p className="text-xl text-gray-700 font-medium">
                        {post.description}
                      </p>
                    )}
                    
                    {/* Sample content - in real app this would come from the database */}
                    <p>
                      Agricultural technology is revolutionizing farming practices across Africa, 
                      bringing unprecedented efficiency and productivity to smallholder farmers 
                      and large-scale agricultural enterprises alike.
                    </p>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                      The Impact of Technology on African Agriculture
                    </h2>
                    
                    <p>
                      From precision farming techniques to IoT-enabled monitoring systems, 
                      technology is helping African farmers overcome traditional challenges 
                      and achieve remarkable results.
                    </p>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                      Key Benefits Include:
                    </h3>
                    
                    <ul className="list-disc list-inside space-y-2 text-gray-600">
                      <li>Increased crop yields through data-driven decisions</li>
                      <li>Reduced water consumption with smart irrigation</li>
                      <li>Early pest and disease detection</li>
                      <li>Optimized resource allocation</li>
                      <li>Improved market access and pricing</li>
                    </ul>
                    
                    <blockquote className="border-l-4 border-green-500 pl-4 italic text-gray-700 my-6">
                      "The integration of technology in agriculture is not just an option anymore—it's a necessity 
                      for sustainable food production in Africa."
                    </blockquote>
                    
                    <p>
                      As we continue to develop and implement innovative solutions, the future of African 
                      agriculture looks brighter than ever. Farmers who embrace these technologies are 
                      seeing significant improvements in their livelihoods and contributing to food 
                      security across the continent.
                    </p>
                  </div>
                )}
              </div>

              {/* Call to Action */}
              <div className="mt-12 p-6 bg-green-50 rounded-xl border border-green-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Ready to Transform Your Farm?
                </h3>
                <p className="text-gray-600 mb-4">
                  Get in touch with our experts to learn how our agricultural technology 
                  solutions can benefit your specific needs.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Get Free Consultation
                  </Link>
                  <Link
                    to="/services"
                    className="border border-green-600 text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors"
                  >
                    Explore Our Services
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
                Related Articles
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Continue reading more about {post.category}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost, index) => (
                  <motion.article
                    key={relatedPost._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    {relatedPost.image && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={relatedPost.image.startsWith('http') ? relatedPost.image : `https://agri-back-mauve.vercel.app${relatedPost.image}`}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                    )}
                    
                    <div className="p-6">
                      {relatedPost.category && (
                        <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full mb-3">
                          {relatedPost.category}
                        </span>
                      )}
                      
                      <h3 className="font-bold text-lg text-gray-900 mb-3 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {relatedPost.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{formatDate(relatedPost.publishedAt)}</span>
                        {relatedPost.readTime && (
                          <span>{relatedPost.readTime} min read</span>
                        )}
                      </div>
                      
                      <Link
                        to={`/blog/${relatedPost.seo?.slug || relatedPost._id}`}
                        className="mt-4 inline-block text-green-600 hover:text-green-700 font-medium text-sm"
                      >
                        Read More →
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Newsletter Subscription */}
      <section className="py-12 bg-green-600">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center text-white"
          >
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Get the latest Afrikonsults insights, research, and success stories delivered directly to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-green-300 focus:outline-none focus:ring-2 focus:ring-white text-gray-900"
              />
              <button className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BlogPost;